import { createClient } from '@citrite/http';
import {
	AiAgentConfigAudience,
	AiAgentConfigValue,
	DataPageResult,
} from '../models/models';

export interface SafRegistryClient {
	/**
	 * List AI agent config values by agent namespace and name
	 * @param audience The audience for which the config is intended
	 * @param namespace The agent namespace
	 * @param name The agent name
	 * @param version The agent version
	 * @param ac AbortController instance to allow cancelling the request
	 * @returns Promise resolving to a paged list of config values
	 */
	listConfigValuesByAgent(
		audience: AiAgentConfigAudience,
		namespace: string,
		name: string,
		version: string,
		ac: AbortController
	): Promise<DataPageResult<AiAgentConfigValue>>;

	/**
	 * Get a specific AI agent config value by key
	 * @param key The config key
	 * @param namespace The agent namespace
	 * @param name The agent name
	 * @param version The agent version
	 * @param ac AbortController instance to allow cancelling the request
	 * @returns Promise resolving to the config value or null if not found
	 */
	getConfigValueByKey(
		key: string,
		namespace: string,
		name: string,
		version: string,
		ac: AbortController
	): Promise<AiAgentConfigValue | null>;

	/**
	 * Set or update an AI agent config value
	 * @param configValue The config value to set
	 * @param ac AbortController instance to allow cancelling the request
	 * @returns Promise resolving to the updated config value
	 */
	setConfigValue(
		configValue: AiAgentConfigValue,
		ac: AbortController
	): Promise<AiAgentConfigValue>;
}

export interface SafRegistryClientConfiguration {
	baseUrl: string;
}

export function initializeClient(
	config: SafRegistryClientConfiguration
): SafRegistryClient {
	const safRegistryHttpClient = createClient({
		baseURL: config.baseUrl,
		timeout: 60000,
		headers: {
			'x-bff-csrf': 'true',
		},
	});

	const aiAgentConfigValuesApiPrefix = 'api/v2/resources/aiagentconfigvalue';

	return {
		async listConfigValuesByAgent(
			audience: AiAgentConfigAudience,
			namespace: string,
			name: string,
			version: string,
			ac: AbortController
		): Promise<DataPageResult<AiAgentConfigValue>> {
			const params = new URLSearchParams();
			params.append('audience', audience);
			params.append('namespace', namespace);
			params.append('name', name);
			params.append('version', version);
			params.append('name', name);

			return await safRegistryHttpClient.get(
				`${aiAgentConfigValuesApiPrefix}?${params.toString()}`,
				{
					signal: ac.signal,
				}
			);
		},

		async getConfigValueByKey(
			key: string,
			namespace: string,
			name: string,
			version: string,
			ac: AbortController
		): Promise<AiAgentConfigValue | null> {
			const params = new URLSearchParams();
			params.append('namespace', namespace);
			params.append('name', name);
			params.append('version', version);

			try {
				return await safRegistryHttpClient.get(
					`${aiAgentConfigValuesApiPrefix}/bykey/${encodeURIComponent(
						key
					)}?${params.toString()}`,
					{
						signal: ac.signal,
					}
				);
			} catch (error: any) {
				if (error.status === 404) {
					return null;
				}
				throw error;
			}
		},

		async setConfigValue(
			configValue: AiAgentConfigValue,
			ac: AbortController
		): Promise<AiAgentConfigValue> {
			return await safRegistryHttpClient.put(
				`${aiAgentConfigValuesApiPrefix}/bykey`,
				configValue,
				{
					signal: ac.signal,
				}
			);
		},
	};
}
